import React, { useEffect, useRef, useState } from "react";
import { STRINGS } from "../../strings";
import { Button } from "../../../../components/Buttons";
import CustomModal from "../../../../components/Modals/CustomModal";
import classes from "./CrmComp.module.css";
import TypographyInline from "../../../../components/Typography/TypographyInline";
import { ERROR, GREY } from "../../../../utils/constants/colors";
import { ICON_LABELS, fontWeight } from "../../../../utils/constants/UI";
import CustomAutoCompleteChip from "../../../../components/Autocomplete/CustomAutoCompleteChip";
import { debounceHelper, getContactsNames } from "../../../../utils/helpers";
import {
  DEBOUNCE_DELAY,
  EMITTER_ERROR,
  EMITTER_SUCCESS,
  MAX_FILE_UPLOAD_LIMIT,
  getFileExtension,
  imageType,
} from "../../../../utils/constants";
import { getRequest, postRequest } from "../../../../Apis";
import {
  authEndpoints,
  CampaignSettingsEndpoints,
  crmEndpoints,
  meetingsEndpoint,
} from "../../../../utils/constants/httpConstants";
import { AutoCompleteCustom } from "../../../../components/Autocomplete/AutoCompleteCustom";
import { useNavigate } from "react-router";
import InputField from "../../../../components/InputFields/InputField";
import Checkbox from "../../../../components/Buttons/Checkbox/Checkbox";
import { HtmlEditor } from "../../../../components";
import EventEmitter from "../../../../utils/emitter/EventEmitter";
import { Uploader } from "../../../../utils/helpers/Uploader";
import { nameValidation } from "../../../../utils/validations";
import CustomMenu from "../../../../components/Menu/CustomMenu";
import CustomDatePicker from "../../../../components/DatePicker/CustomDatePicker";
import TimeSelector from "../../../../components/TimeSelector/TimeSelector";
import SkeletonLoaderCRMScreens from "./SkeletonLoaderCRMScreens";
import { Box } from "@mui/material";
import Image from "../../../../components/Image/Image";
import { GMAIL_IMG, OUTLOOK_CONNECT } from "../../../../utils/constants/assets";

// Marks the signature block inside the editor so it can be swapped out cleanly.
const SIGNATURE_NODE_ATTR = "data-riq-crm-signature";

// A signature document stores EITHER pre-rendered html (`signature_html`, or
// `signature` as a string) OR a structured object of fields (`signature` as an
// object). Assigning that object straight into the editor is what put the literal
// "[object Object]" in the compose body - and, because that text was then sent, is
// why the delivered mail carried no signature either.
// Mirrors buildSignatureHtml in salesSequenceSendWorker so the composer and the
// sequence sender render the same block.
const sigText = (v) => {
  if (v === null || v === undefined) return "";
  const t = String(v).trim();
  return t.toLowerCase() === "null" || t.toLowerCase() === "undefined" ? "" : t;
};
const sigLine = (val) =>
  val
    ? `<p style="font-weight:400;font-size:14px;color:#626b7e;line-height:24px;margin:0;">${val}</p>`
    : "";

const resolveSignatureHtml = (doc) => {
  if (!doc) return "";
  let html = "";
  if (typeof doc.signature_html === "string" && doc.signature_html.length) {
    html = doc.signature_html;
  } else if (typeof doc.signature === "string" && doc.signature.length) {
    html = doc.signature;
  }
  if (html && html.includes("%3C")) {
    try {
      html = decodeURIComponent(html);
    } catch (_) {
      /* keep the raw value */
    }
  }
  const details =
    doc.signature && typeof doc.signature === "object" ? doc.signature : null;
  let top = "";
  if (details && !html) {
    const fullName = [sigText(details.first_name), sigText(details.last_name)]
      .filter(Boolean)
      .join(" ");
    const image = sigText(details.image_url);
    const cityParts = [
      sigText(details.city),
      sigText(details.state),
      sigText(details.country),
      sigText(details.zip_code),
    ]
      .filter(Boolean)
      .join(", ");
    top =
      (fullName
        ? `<h5 style="font-weight:500;font-size:18px;color:#212833;margin-bottom:0;">${fullName}</h5>`
        : "") +
      sigLine(sigText(details.job_title)) +
      (image
        ? `<p style="margin:0;padding:4px 0;"><img src="${image}" alt="${sigText(details.image_name) || "Logo"}" style="max-width:200px;height:auto;display:block;" /></p>`
        : "") +
      sigLine(sigText(details.company_name)) +
      sigLine(sigText(details.phone_number)) +
      sigLine(sigText(details.address_line_1)) +
      sigLine(sigText(details.address_line_2)) +
      sigLine(cityParts);
  }
  if (!top && !html) return "";
  return `<div>${top}${html}</div>`;
};

const EmailSendModal = ({
  open = false,
  close = () => { },
  contactId = "",
  getEmails = () => { },
  data = {},
  setPageCount = () => { },
  replyType = "",
  contactDetails,
  postAction = () => { },
}) => {
  const navigate = useNavigate();
  const [sendEmailLoading, setSendEmailLoading] = useState(false);
  const [contactList, setContactList] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [ccVisible, setCcVisible] = useState(false);
  const [bccVisible, setBccVisible] = useState(false);
  const [connectedInbox, setConnectedInbox] = useState([]);
  const [toContacts, setToContacts] = useState([]);
  const [ccContacts, setCcContacts] = useState([]);
  const [bccContacts, setBccContacts] = useState([]);
  const [fromContacts, setFromContacts] = useState({});
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [errors, setErrors] = useState({});
  const [sendEmailOperationTarget, setSendEmailOperationTarget] =
    useState(null);
  const [scheduleTimeSelectModal, setScheduleTimeSelectModal] = useState(false);
  const [sendEmailDate, setSendEmailDate] = useState(new Date());
  const [sendEmailTime, setSendEmailTime] = useState("00:00");
  const [selectedTo, setSelectedTo] = useState([]);
  const [selectedCc, setSelectedCc] = useState([]);
  const [selectedBcc, setSelectedBcc] = useState([]);
  const [attachments, setAttachments] = useState({});
  const [loading, setLoading] = useState(true);
  const [isInboxConnected, setIsInboxConnected] = useState(false);
  const isReply = Boolean(Object.keys(data)?.length);

  // Signatures saved under Profile → Email Signatures, usable straight from the
  // composer. The picked one is wrapped in a marked node so switching or clearing
  // replaces it instead of stacking signatures at the bottom of the mail.
  const [signatureList, setSignatureList] = useState([]);
  const [signatureLabel, setSignatureLabel] = useState([]);
  const [signatureId, setSignatureId] = useState([]);
  // Opt-out line. On by default - a cold email the recipient cannot opt out of is the thing we are
  // trying not to send. Unchecking sends include_unsubscribe_line:false and the backend leaves the
  // body exactly as written.
  const [includeUnsubscribeLine, setIncludeUnsubscribeLine] = useState(true);
  // Which From-mailbox the editor's signature block currently belongs to. A plain
  // "already applied" boolean meant switching From refreshed the picker's options
  // but left the PREVIOUS mailbox's signature sitting in the body, forcing a manual
  // re-pick. Tracking the mailbox lets each switch re-apply exactly once.
  const signatureAutoAppliedFor = useRef(null);
  const [signatureLoading, setSignatureLoading] = useState(false);

  // The signature block is NOT editable in the composer (it is managed under
  // Settings and swapped with the picker below the editor).
  //
  // Why: while it was editable the caret landed inside it — the block is appended
  // last and an empty compose has nothing else to click into. Pressing Enter there
  // makes the browser SPLIT the block and clone its attributes, so the rep's own
  // paragraphs ended up carrying SIGNATURE_NODE_ATTR too. The next re-apply then
  // removed the FIRST marked node, which was the rep's message, and left an empty
  // signature shell behind — that shell is exactly what shipped in the mails with
  // no signature. contentEditable=false makes the caret skip the block entirely,
  // so it can neither be split nor typed over.
  const applySignatureToEditor = (signatureHtml) => {
    const editor = document.getElementById(STRINGS.EMAIL_SEND_MODAL_EDITOR_ID);
    if (!editor) return;
    // querySelectorAll, not querySelector: older drafts can still hold the cloned
    // markers from before this change, and leaving one behind stacks signatures.
    editor
      .querySelectorAll(`[${SIGNATURE_NODE_ATTR}]`)
      .forEach((node) => node.remove());
    if (typeof signatureHtml === "string" && signatureHtml.trim()) {
      // A non-editable block as the ONLY child leaves the rep with nowhere to click,
      // so guarantee an editable line above it on a fresh compose.
      if (!editor.textContent.trim() && !editor.querySelector("img")) {
        editor.innerHTML = "<div><br></div>";
      }
      const holder = document.createElement("div");
      holder.setAttribute(SIGNATURE_NODE_ATTR, "1");
      holder.setAttribute("contenteditable", "false");
      holder.innerHTML = `<br/>${signatureHtml}`;
      editor.appendChild(holder);
    }
    setBody(editor.innerHTML);
  };

  // What actually goes on the wire. The marker + contenteditable flag are composer
  // plumbing; strip them so the delivered mail carries plain signature markup.
  const getEditorHtmlForSend = () => {
    const editor = document.getElementById(STRINGS.EMAIL_SEND_MODAL_EDITOR_ID);
    if (!editor) return "";
    const clone = editor.cloneNode(true);
    clone.querySelectorAll(`[${SIGNATURE_NODE_ATTR}]`).forEach((node) => {
      node.removeAttribute(SIGNATURE_NODE_ATTR);
      node.removeAttribute("contenteditable");
    });
    return clone.innerHTML;
  };

  // Never send from a mailbox the user doesn't own. Composing/replying off a
  // marketing-campaign email row carries that campaign's POOLED from_email, and two
  // concurrent effects race to set `fromContacts` — when the pooled address won, the
  // send failed with `invalid_grant` (the user's OAuth token doesn't cover it).
  // Fall back to the user's primary connected inbox.
  const resolveFromEmail = () => {
    const owned = (connectedInbox || []).some(
      (i) => i?.email && i.email === fromContacts?.email
    );
    if (owned) return fromContacts?.email;
    // Prefer the primary inbox; if none is flagged primary, any owned inbox still beats an
    // unowned (campaign-pool) address, which is what triggers invalid_grant.
    const primary = (connectedInbox || []).filter((i) => i?.is_primary)?.[0];
    return primary?.email || (connectedInbox || [])[0]?.email || fromContacts?.email;
  };

  const validateRequiredFieldHandler = (send_now) => {
    // checking required fields
    const validateBody = nameValidation(encodeURIComponent(getEditorHtmlForSend()));
    const validateSubject = nameValidation(subject);
    const validateToContacts = nameValidation(toContacts);
    const validateFromContacts = nameValidation(fromContacts.email);

    if (
      validateBody?.isValid &&
      validateFromContacts?.isValid &&
      validateSubject?.isValid &&
      validateToContacts?.isValid
    ) {
      sendEmailHandler(send_now);
    } else {
      setErrors({
        toContacts: validateToContacts?.message,
        fromContacts: validateFromContacts?.message,
        subject: validateSubject?.message,
        body: validateBody?.message,
      });
      setSendEmailLoading(false);
    }
  };

  const sendEmailHandler = async (send_now, reply_type = replyType) => {
    try {
      let toEmails = toContacts?.map((item) => {
        return item?.email;
      });
      let ccEmails = ccContacts?.map((item) => {
        return item?.email;
      });
      let bccEmails = bccContacts?.map((item) => {
        return item?.email;
      });
      let processing_date_time = new Date().getTime();

      if (!send_now) {
        let processingDateTime = `${sendEmailDate?.toDateString()} ${sendEmailTime}`;
        processing_date_time = new Date(processingDateTime).getTime();
      }

      setErrors({});

      let payload = {
        body: encodeURIComponent(getEditorHtmlForSend()),
        contact_id: contactId,
        subject: subject,
        send_now: send_now,
        from_email: resolveFromEmail(),
        to_emails: toEmails,
        cc_emails: reply_type === STRINGS.REPLY_SMALL ? [] : ccEmails,
        bcc_emails: reply_type === STRINGS.REPLY_SMALL ? [] : bccEmails,
        processing_date_time: processing_date_time,
        include_unsubscribe_line: includeUnsubscribeLine,
      };
      if (contactDetails?.company_id) {
        payload.company_id = contactDetails?.company_id;
      }

      // attachments add check
      const attachment_keys = [];

      for (let i in attachments) {
        attachment_keys.push({
          fileKey: attachments[i].uploader.fileKey,
          fileName: attachments[i].uploader.fileName,
        });
      }
      if (attachment_keys.length) {
        payload.attachment_keys = attachment_keys;
      }

      setSendEmailLoading(true);
      const response = await postRequest(
        navigate,
        crmEndpoints.createEmail,
        payload
      );
      EventEmitter.emit(EMITTER_SUCCESS, response?.data?.message);
      getEmails(false, 0);
      setSendEmailLoading(false);
      postAction();
      close();
    } catch (error) {
      setSendEmailLoading(false);
      EventEmitter.emit(EMITTER_ERROR, error?.data?.message);
      console.log("Error ->>>> ", error);
    }
  };

  const replyEmailHandler = async (reply_type = replyType) => {
    try {
      let toEmails = toContacts?.map((item) => {
        return item?.email;
      });
      let ccEmails = ccContacts?.map((item) => {
        return item?.email;
      });
      let bccEmails = bccContacts?.map((item) => {
        return item?.email;
      });

      let payload = {
        body: encodeURIComponent(getEditorHtmlForSend()),
        subject: subject,
        from_email: resolveFromEmail(),
        to_emails: toEmails,
        cc_emails: reply_type === STRINGS.REPLY_SMALL ? [] : ccEmails,
        bcc_emails: reply_type === STRINGS.REPLY_SMALL ? [] : bccEmails,
        message_id: data?.message_id,
      };

      // attachments add check
      const attachment_keys = [];
      for (let i in attachments) {
        attachment_keys.push({
          fileKey: attachments[i].uploader.fileKey,
          fileName: attachments[i].uploader.fileName,
        });
      }
      if (attachment_keys.length) {
        payload.attachment_keys = attachment_keys;
      }

      setSendEmailLoading(true);
      const response = await postRequest(
        navigate,
        crmEndpoints.replyOnEmail,
        payload
      );
      EventEmitter.emit(EMITTER_SUCCESS, response?.data?.message);
      getEmails(false, 0);
      setSendEmailLoading(false);
      close();
    } catch (error) {
      setSendEmailLoading(false);
      EventEmitter.emit(EMITTER_ERROR, error?.data?.message);
      console.log("Error ->>>> ", error);
    }
  };

  const getContactsList = async (value = "") => {
    try {
      const response = await getRequest(
        navigate,
        crmEndpoints.getEmailSuggestions + `?suggestion=${value}`
      );
      setContactList(response?.data?.data?.records);
      return response?.data?.data?.records;
    } catch (error) { }
  };

  const createSendActionHandler = (e) => {
    if (isReply) {
      setPageCount(0);
      replyEmailHandler();
    } else {
      setSendEmailOperationTarget(e?.currentTarget);
    }
  };

  const sendEmailModalHeader = {
    heading: {
      id: isReply ? STRINGS.REPLY_EMAIL_SMALL : STRINGS.EMAIL,
      label: isReply ? STRINGS.REPLY_EMAIL : STRINGS.EMAIL,
    },
  };

  const sendEmailModalRightFooter = isInboxConnected ? (
    <div className={classes.modalFooterRightData}>
      <Button
        label={STRINGS.CANCEL}
        variant={"ghost"}
        size={"sm36"}
        onClick={close}
      />
      <Button
        label={isReply ? STRINGS.SEND : STRINGS.CREATE}
        variant={"primary"}
        size={"sm36"}
        RightIcon={isReply ? "" : ICON_LABELS.ACTION_ARROW}
        onClick={createSendActionHandler}
        isLoading={sendEmailLoading}
      />
    </div>
  ) : null;

  const scheduleEmailModalHeader = {
    heading: {
      id: STRINGS.SECHEDULE_SEND_SMALL,
      label: STRINGS.SCHEDULE_SEND,
    },
  };

  const scheduleEmailModalRightFooter = (
    <div className={classes.modalFooterRightData}>
      <Button
        label={STRINGS.CANCEL}
        variant={"ghost"}
        size={"sm36"}
        onClick={() => {
          setScheduleTimeSelectModal(false);
        }}
      />
      <Button
        label={STRINGS.SCHEDULE}
        variant={"primary"}
        size={"sm36"}
        onClick={() => {
          validateRequiredFieldHandler(false);
          setScheduleTimeSelectModal(false);
        }}
        isLoading={sendEmailLoading}
      />
    </div>
  );

  const emailSendOperation = [
    {
      id: STRINGS.SEND_NOW_SMALL,
      label: STRINGS.SEND_NOW,
      iconLabel: ICON_LABELS.SEND,
      function: () => validateRequiredFieldHandler(true),
    },
    {
      id: STRINGS.SECHEDULE_SEND_SMALL,
      label: STRINGS.SCHEDULE_SEND,
      iconLabel: ICON_LABELS.SEND_AND_ARCHIVE,
      function: () => setScheduleTimeSelectModal(true),
    },
  ];

  const debounceSearch = debounceHelper(getContactsList, DEBOUNCE_DELAY);

  const handleAttachmentsUpload = (e) => {
    try {
      // upload files here.
      let files = e.target.files || [];
      let sumOfsize = 0;

      // file size check.
      for (let i in files) {
        sumOfsize += files[i].size || 0;
      }

      if (sumOfsize < MAX_FILE_UPLOAD_LIMIT) {
        const temp_files = {};
        for (let i = 0; i < files.length; i++) {
          const extension = getFileExtension(files[i]?.name);
          const videoUploaderOptions = {
            fileName: files?.[i]?.name || STRINGS.FOO,
            size: files?.[i]?.size || 0,
            file: files?.[i],
            contact_id: contactId,
            typeImage: imageType[extension] ? "image" : "doc",
            initialEndpoint: crmEndpoints.uploadCrmFileInitialEndpoint,
            multipartEndpoint: crmEndpoints.uploadCrmFileMultipartEndpoint,
            finalEndpoint: crmEndpoints.uploadCrmFilefinalEndpoint,
            navigate: navigate,
          };
          if (contactDetails?.company_id) {
            videoUploaderOptions.company_id = contactDetails?.company_id;
          }

          // uploader
          const uploader = new Uploader(videoUploaderOptions);
          if (uploader.fileName) {
            temp_files[uploader.fileName] = {
              uploader,
              percentage: 0,
              isFinish: false,
            };
          }
        }
        for (let i in temp_files) {
          temp_files[i].uploader.start();
        }
        setAttachments(temp_files);
      } else {
        EventEmitter.emit(EMITTER_ERROR, STRINGS.FILE_SIZE_TOO_LARGE);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleAttachmentDelete = (item) => {
    try {
      const parsed_attachments = { ...attachments };
      delete parsed_attachments[item.name];
      setAttachments(parsed_attachments);

      // make api call to delete from db.
    } catch (error) { }
  };

  const handleGoogle = () => {
    const URL =
      "https://" +
      process.env.REACT_APP_DEV_REGISTRATION_LINK_WITHOUT_PROTOCAL +
      "/auth/connect-inbox/gmail/" +
      window.location.host +
      "?tenant_path=" +
      encodeURIComponent(
        `${window.location.pathname + window.location.search}`
      );
    const anchor = document.createElement("a");
    anchor.href = URL;
    anchor.click();
  };

  const handleMicrosoft = () => {
    const URL =
      "https://" +
      process.env.REACT_APP_DEV_REGISTRATION_LINK_WITHOUT_PROTOCAL +
      "/auth/connect-inbox/outlook/" +
      window.location.host +
      "?tenant_path=" +
      encodeURIComponent(
        `${window.location.pathname + window.location.search}`
      );
    const anchor = document.createElement("a");
    anchor.href = URL;
    anchor.click();
  };

  useEffect(() => {
    if (open) {
      if (contactDetails?.email) {
        setSelectedTo([
          {
            email: contactDetails?.email || "",
            first_name: contactDetails?.first_name || "",
            last_name: contactDetails?.last_name || "",
          },
        ]);
      }
      (async () => {
        const response = await getContactsList();

        // Filling all the values in the fields when we are replying on the mail.
        if (isReply) {
          let to_emails = data?.to_emails;
          let cc_emails = data?.cc_emails;
          let bcc_emails = data?.bcc_emails;
          let from_email = data?.from_email;

          // vairable for array of object to be stored as a default values of autocomplete.
          let selected_to = [];
          let selected_cc = [];
          let selected_bcc = [];
          let selected_from = {
            email: from_email,
            id: from_email,
            label: from_email,
          };

          // storing all the emails into an array and appending all into contactLists array.
          let arr = [];
          to_emails?.forEach((element) => {
            let obj = {};
            obj.email = element;
            arr.push(obj);
            selected_to.push(obj);
          });
          cc_emails?.forEach((element) => {
            let obj = {};
            obj.email = element;
            arr.push(obj);
            selected_cc.push(obj);
          });
          bcc_emails?.forEach((element) => {
            let obj = {};
            obj.email = element;
            arr.push(obj);
            selected_bcc.push(obj);
          });
          // visibility on if cc or bcc emails are available.
          if (cc_emails?.length && replyType === STRINGS.REPLY_ALL_SMALL)
            setCcVisible(true);
          if (bcc_emails?.length && replyType === STRINGS.REPLY_ALL_SMALL)
            setBccVisible(true);

          setSelectedTo(selected_to);
          if (replyType === STRINGS.REPLY_ALL_SMALL) {
            setSelectedCc(selected_cc);
            setSelectedBcc(selected_bcc);
          }

          setToContacts(selected_to);
          setCcContacts(selected_cc);
          setBccContacts(selected_bcc);
          setFromContacts(selected_from);
          setContactList([...arr, ...response]);
          setSubject(data?.subject);
          document.getElementById(STRINGS.EMAIL_SEND_MODAL_EDITOR_ID).innerHTML = data?.body;
        }
      })();

      (async () => {
        try {
          const response = await getRequest(
            navigate,
            meetingsEndpoint.isCalendarConnected
          );
          setIsInboxConnected(!response.data.data.isConnectionEmpty);
          const limit = await getRequest(
            navigate,
            authEndpoints?.getApiKeyDetails
          );
          let gmail_inboxes = limit?.data?.data?.inboxes.gmail;
          let outlook_inboxes = limit?.data?.data?.inboxes.outlook;
          let zoho_inboxes = limit?.data?.data?.inboxes.zoho;
          let all_inboxes_data = [];
          (gmail_inboxes || []).forEach((element) => {
            all_inboxes_data = [...all_inboxes_data, { ...element }];
          });
          (outlook_inboxes || []).forEach((element) => {
            all_inboxes_data = [...all_inboxes_data, { ...element }];
          });
          (zoho_inboxes || []).forEach((element) => {
            all_inboxes_data = [...all_inboxes_data, { ...element }];
          });
          setConnectedInbox(all_inboxes_data);

          if (all_inboxes_data.length) {
            if (isReply) {
              all_inboxes_data?.filter((item) => item.email === data.from_email)
                ?.length
                ? setIsInboxConnected(true)
                : setIsInboxConnected(false);
              setIsInboxConnected(true);
            }
            // add  reply condition here.

            setFromContacts({
              ...all_inboxes_data?.filter((item) => item?.is_primary)?.[0],
              label: all_inboxes_data?.filter((item) => item?.is_primary)?.[0]
                ?.label,
            });
          } else {
            setIsInboxConnected(false);
          }
        } catch (err) {
          setIsInboxConnected(false);
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [open]);

  // Signatures for the mailbox we are actually sending from. Auto-applies the
  // mailbox default on a fresh compose so the common case costs zero clicks; on a
  // reply the editor already holds the quoted thread, so we leave it alone and let
  // the picker do the inserting.
  useEffect(() => {
    if (!open || loading) return;
    let cancelled = false;
    const activeEmail = resolveFromEmail();
    // Re-apply on every From change, not once per modal: each mailbox has its own
    // default signature.
    const needsAutoApply =
      !isReply && signatureAutoAppliedFor.current !== activeEmail;
    if (needsAutoApply) setSignatureLoading(true);
    (async () => {
      try {
        const accountId = (connectedInbox || []).find(
          (i) => i?.email && i.email === activeEmail,
        )?._id;
        const url = accountId
          ? `${CampaignSettingsEndpoints.signatureList}?account_id=${accountId}`
          : CampaignSettingsEndpoints.signatureList;
        const response = await getRequest(navigate, url);
        if (cancelled) return;
        const responseData = response?.data?.data;
        // Flat array when account_id is passed, map keyed by account_id otherwise.
        const list = Array.isArray(responseData)
          ? responseData
          : responseData && typeof responseData === "object"
            ? Object.values(responseData).flatMap((e) => e?.signatures || [])
            : [];
        const items = list
          .map((item, ind) => ({
            id: item?._id || `sig${ind}`,
            label:
              item?.signature_name || item?.email || `Signature ${ind + 1}`,
            signature: resolveSignatureHtml(item),
            is_default: !!item?.is_default,
          }))
          // A document that renders to nothing is not offerable — picking it would
          // silently insert an empty block.
          .filter((item) => item.signature);
        setSignatureList(items);

        if (needsAutoApply) {
          signatureAutoAppliedFor.current = activeEmail;
          if (items.length) {
            const chosen = items.find((i) => i.is_default) || items[0];
            setSignatureId([chosen.id]);
            setSignatureLabel([chosen.label]);
            applySignatureToEditor(chosen.signature);
          } else {
            // The new mailbox has no signature — drop the previous one rather than
            // signing this mail off as someone else.
            setSignatureId([]);
            setSignatureLabel([]);
            applySignatureToEditor("");
          }
        }
      } catch {
        if (!cancelled) setSignatureList([]);
      } finally {
        if (!cancelled) setSignatureLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, loading, connectedInbox, fromContacts?.email]);

  // Reset the once-only auto-apply when the modal closes.
  useEffect(() => {
    if (!open) {
      signatureAutoAppliedFor.current = null;
      setSignatureId([]);
      setSignatureLabel([]);
      setSignatureLoading(false);
    }
  }, [open]);

  return (
    <div>
      {/* Modal for Creat/Send Email  */}
      <CustomModal
        close={close}
        open={open}
        header={sendEmailModalHeader}
        footer={
          isInboxConnected && {
            right: sendEmailModalRightFooter,
          }
        }
        width={"50.417vw"}
      >
        {loading ? (
          <SkeletonLoaderCRMScreens accordionLengths={6} buttonsLength={0} />
        ) : !isInboxConnected ? (
          <Box>
            <Box className={classes.containGmailBtn}>
              <Box className={classes.gBox}>
                <Image
                  src={GMAIL_IMG}
                  className={classes.image}
                  alt={STRINGS.GLOGO}
                />
                <Box className={classes.CalendarApi}>
                  <TypographyInline
                    size={"xl"}
                    color={GREY.PRIMARY}
                    fontWeight={fontWeight.SEMIBOLD}
                    label={STRINGS.CONNECT_WITH_GOOGLE_CALENDAR}
                  />
                  <TypographyInline
                    size={"sm"}
                    color={GREY.SECONDARY}
                    fontWeight={fontWeight.REGULAR}
                    label={STRINGS.CONNECT_GMAIL_ACC}
                  />
                </Box>
                <Box className={classes.CalendarApiFooter}>
                  <Button
                    label={STRINGS.CONNECT}
                    size={"sm36"}
                    variant={"primary"}
                    onClick={() => {
                      handleGoogle();
                    }}
                  />
                </Box>
              </Box>
              <Box>
                <hr className={classes.hline} />
              </Box>
              <Box className={classes.gBox}>
                <Image
                  src={OUTLOOK_CONNECT}
                  className={classes.image}
                  alt={STRINGS.GLOGO}
                />
                <Box className={classes.CalendarApi}>
                  <TypographyInline
                    size={"xl"}
                    color={GREY.PRIMARY}
                    fontWeight={fontWeight.SEMIBOLD}
                    label={STRINGS.CONNECT_WITH_OUTLOOK_CALENDAR}
                  />
                  <TypographyInline
                    size={"sm"}
                    color={GREY.SECONDARY}
                    fontWeight={fontWeight.REGULAR}
                    label={STRINGS.CONNECT_OUTLOOK_ACC}
                  />
                </Box>
                <Box className={classes.CalendarApiFooter}>
                  <Button
                    label={STRINGS.CONNECT}
                    size={"sm36"}
                    variant={"primary"}
                    onClick={() => handleMicrosoft()}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        ) : (
          <div className={classes.logEmailBody}>
            {/* To Field */}
            <div className={classes.sendEmailLabelFieldContainer}>
              <div className={classes.sendEmailModalLabelDiv}>
                <TypographyInline
                  color={GREY.SECONDARY}
                  label={STRINGS.TO_CAPITAL}
                  size={"sm"}
                  fontWeight={fontWeight.SEMIBOLD}
                />
              </div>
              {(selectedTo?.length || !isReply) && (
                <CustomAutoCompleteChip
                  id="to_list"
                  items={getContactsNames(contactList)}
                  onChange={(data) => {
                    setToContacts([...data]);
                  }}
                  inputOnchange={(value) => {
                    setSearchValue(value);
                    if (!isReply) {
                      debounceSearch(value);
                    }
                  }}
                  placeholder={STRINGS.TYPE_TO_SEARCH}
                  errormessage={errors?.toContacts}
                  variant={errors?.toContacts ? STRINGS.ERROR : STRINGS.DEFAULT}
                  defaultValues={getContactsNames(selectedTo)}
                />
              )}
              <div className={classes.ccBccText}>
                {!ccVisible ? (
                  <div
                    className={classes.ccBccLabel}
                    onClick={() => setCcVisible(true)}
                  >
                    <TypographyInline
                      color={GREY.SECONDARY}
                      label={STRINGS.CC}
                      size={"sm"}
                      fontWeight={fontWeight.SEMIBOLD}
                    />
                  </div>
                ) : null}
                {!bccVisible ? (
                  <div
                    className={classes.ccBccLabel}
                    onClick={() => setBccVisible(true)}
                  >
                    <TypographyInline
                      color={GREY.SECONDARY}
                      label={STRINGS.BCC}
                      size={"sm"}
                      fontWeight={fontWeight.SEMIBOLD}
                    />
                  </div>
                ) : null}
              </div>
            </div>

            {/* CC Field */}
            {ccVisible && (
              <div className={classes.sendEmailLabelFieldContainer}>
                <div className={classes.sendEmailModalLabelDiv}>
                  <TypographyInline
                    color={GREY.SECONDARY}
                    label={STRINGS.CC}
                    size={"sm"}
                    fontWeight={fontWeight.SEMIBOLD}
                  />
                </div>
                {(selectedCc?.length || !isReply) && (
                  <CustomAutoCompleteChip
                    id="cc_list"
                    items={getContactsNames(contactList)}
                    onChange={(data) => {
                      setCcContacts([...data]);
                    }}
                    inputOnchange={(value) => {
                      setSearchValue(value);
                      if (!isReply) {
                        debounceSearch(value);
                      }
                    }}
                    placeholder={STRINGS.TYPE_TO_SEARCH}
                    defaultValues={getContactsNames(selectedCc)}
                    multiple
                  />
                )}
              </div>
            )}

            {/* BCC Field */}
            {bccVisible && (
              <div className={classes.sendEmailLabelFieldContainer}>
                <div className={classes.sendEmailModalLabelDiv}>
                  <TypographyInline
                    color={GREY.SECONDARY}
                    label={STRINGS.BCC}
                    size={"sm"}
                    fontWeight={fontWeight.SEMIBOLD}
                  />
                </div>
                {(selectedBcc?.length || !isReply) && (
                  <CustomAutoCompleteChip
                    id="bcc_list"
                    items={getContactsNames(contactList)}
                    onChange={(data) => {
                      setBccContacts([...data]);
                    }}
                    inputOnchange={(value) => {
                      setSearchValue(value);
                      if (!isReply) {
                        debounceSearch(value);
                      }
                    }}
                    placeholder={STRINGS.TYPE_TO_SEARCH}
                    defaultValues={getContactsNames(selectedBcc)}
                    multiple
                  />
                )}
              </div>
            )}

            {/* From Field */}
            <div className={classes.sendEmailLabelFieldContainer}>
              <div className={classes.sendEmailModalLabelDiv}>
                <TypographyInline
                  color={GREY.SECONDARY}
                  label={STRINGS.FROM}
                  size={"sm"}
                  fontWeight={fontWeight.SEMIBOLD}
                />
              </div>
              {(fromContacts?.email?.length || !isReply) && (
                <CustomAutoCompleteChip
                  id="from_list"
                  items={getContactsNames(connectedInbox, false)}
                  onChange={(data) => {
                    setFromContacts(data);
                  }}
                  placeholder={STRINGS.TYPE_TO_SEARCH}
                  errormessage={errors?.fromContacts}
                  variant={
                    errors?.fromContacts ? STRINGS.ERROR : STRINGS.DEFAULT
                  }
                  defaultValues={fromContacts}
                  multiple={false}
                />
              )}
            </div>

            {/* Subject Field */}
            <div className={classes.sendEmailSubjectBodyContainer}>
              <div className={classes.sendEmailModalLabelDiv}>
                <TypographyInline
                  color={GREY.SECONDARY}
                  label={STRINGS.SUBJECT}
                  size={"sm"}
                  fontWeight={fontWeight.SEMIBOLD}
                  isRequired
                />
              </div>
              <InputField
                size={"sm36"}
                onChange={(e) => {
                  setSubject(e?.target?.value);
                }}
                value={subject}
                placeholder={STRINGS.TYPE_SUBJECT}
                errormessage={errors?.subject}
                variant={errors?.subject ? STRINGS.ERROR : STRINGS.DEFAULT}
              />
            </div>
            {/* Body Field */}
            <div className={classes.sendEmailSubjectBodyContainer}>
              <div className={classes.sendEmailModalLabelDiv}>
                <TypographyInline
                  color={GREY.SECONDARY}
                  label={STRINGS.BODY}
                  size={"sm"}
                  fontWeight={fontWeight.SEMIBOLD}
                  isRequired
                />
              </div>
              <div className={classes.errorAndEditor}>
                <div className={classes.htmlEditor}>
                  {/* Meeting link + unsubscribe tag are offered here now. The editor already knows
                      how to insert both - the meeting one asks for the URL AND the display text, so
                      the sender wraps their own wording around it - and the backend turns the
                      unsubscribe tag into a link minted for this recipient at send time. They were
                      simply hidden before. */}
                  <HtmlEditor
                    onInput={(data) => setBody(data?.target?.innerHTML)}
                    editorHeight={"20vh"}
                    id={STRINGS.EMAIL_SEND_MODAL_EDITOR_ID}
                    isAITagNotVisible
                    isAddTagNotVisible
                    isUnsubscirbeTagVisible
                    onAttachmentChange={handleAttachmentsUpload}
                    handleDeleteAttachment={handleAttachmentDelete}
                    attachments={Object.keys(attachments).map((item, index) => {
                      return {
                        name: item,
                        id: attachments[item].fileKey || index,
                      };
                    })}
                  />
                </div>
                {/* Signature picker. Sits right under the editor so the inserted
                    block is visible while composing; picking a different one
                    swaps it in place. */}
                {signatureList?.length || signatureLoading ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginTop: "8px",
                    }}
                  >
                    <TypographyInline
                      label={STRINGS.SIGNATURE_LABEL}
                      color={GREY.SECONDARY}
                      size={"sm"}
                      fontWeight={fontWeight.SEMIBOLD}
                    />
                    {/* Switching the From mailbox refetches its signatures; say so
                        instead of leaving the picker showing the old mailbox's pick. */}
                    {signatureLoading ? (
                      <TypographyInline
                        label={STRINGS.UPDATING_SIGNATURE}
                        color={GREY.SECONDARY}
                        size={"sm"}
                        fontWeight={fontWeight.REGULAR}
                      />
                    ) : (
                      <div style={{ flex: "0 0 240px", minWidth: 0 }}>
                        <AutoCompleteCustom
                          items={signatureList}
                          valueState={signatureLabel}
                          setValueState={setSignatureLabel}
                          idState={signatureId}
                          setIdState={(ids) => {
                            setSignatureId(ids);
                            const picked = signatureList.find(
                              (s) => s.id === ids?.[0],
                            );
                            applySignatureToEditor(picked?.signature || "");
                          }}
                          size="sm36"
                          placeholder={STRINGS.SELECT_SIGNATURE}
                        />
                      </div>
                    )}
                  </div>
                ) : null}
                {/* The line itself is appended by the backend at send time, not injected into the
                    editor: the unsubscribe URL carries this recipient's address inside a signed
                    token, so it cannot exist until we know who the mail is going to. What the
                    sender needs here is to know it will be there, and to be able to say no. */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginTop: "8px",
                  }}
                >
                  <Checkbox
                    noMargin
                    checked={includeUnsubscribeLine}
                    onChange={() =>
                      setIncludeUnsubscribeLine(!includeUnsubscribeLine)
                    }
                    aria-label={STRINGS.INCLUDE_UNSUBSCRIBE_LINE}
                  />
                  <div
                    style={{ cursor: "pointer" }}
                    onClick={() =>
                      setIncludeUnsubscribeLine(!includeUnsubscribeLine)
                    }
                  >
                    <TypographyInline
                      label={STRINGS.INCLUDE_UNSUBSCRIBE_LINE}
                      color={GREY.SECONDARY}
                      size={"sm"}
                      fontWeight={fontWeight.MEDIUM}
                    />
                  </div>
                </div>
                {errors?.body ? (
                  <div>
                    <TypographyInline
                      label={errors?.body}
                      color={ERROR.W_500}
                      size={"xs"}
                      fontWeight={fontWeight.REGULAR}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        )}
      </CustomModal>

      {/* Create/Send Email Operations */}
      <CustomMenu
        items={emailSendOperation}
        anchor={sendEmailOperationTarget}
        open={Boolean(sendEmailOperationTarget)}
        onClose={() => {
          setSendEmailOperationTarget(null);
        }}
        paperMenuClass={classes.scheduleNowOperation}
      />

      {/* Schedule Time Select Modal */}
      <CustomModal
        close={() => setScheduleTimeSelectModal(false)}
        open={scheduleTimeSelectModal}
        header={scheduleEmailModalHeader}
        footer={{
          right: scheduleEmailModalRightFooter,
        }}
        width={"35.260vw"}
      >
        <div className={classes.scheduleTimeMainContainer}>
          {/* Select Date */}
          <div className={classes.inputLabelField}>
            <TypographyInline
              color={GREY.SECONDARY}
              label={STRINGS.DUE_DATE}
              size={"sm"}
              fontWeight={fontWeight.REGULAR}
            />
            <CustomDatePicker
              size={"sm36"}
              value={sendEmailDate}
              onChange={(dateValue) => {
                setSendEmailDate(new Date(dateValue));
              }}
            />
          </div>

          {/* Select Time */}
          <div className={classes.inputLabelField}>
            <TypographyInline
              color={GREY.SECONDARY}
              label={STRINGS.TIME}
              size={"sm"}
              fontWeight={fontWeight.REGULAR}
            />
            <TimeSelector
              time={sendEmailTime}
              setTime={(time) => {
                setSendEmailTime(time);
              }}
              isAbsolute={false}
            />
          </div>
        </div>
      </CustomModal>
    </div>
  );
};

export default EmailSendModal;
